import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import { motion } from 'framer-motion';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';

const DatePickerPopup = ({ open, onClose, onDateSelect, currentDate, today, monthNames }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedDate, setSelectedDate] = useState(currentDate);
  const [monthEvents, setMonthEvents] = useState({});

  // 月の制限をチェック
  const oneYearAgo = new Date(today);
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
  
  const threeMonthsAhead = new Date(today);
  threeMonthsAhead.setMonth(threeMonthsAhead.getMonth() + 3);

  const canGoPrev = () => {
    const prevMonth = new Date(selectedDate);
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    return prevMonth >= oneYearAgo;
  };

  const canGoNext = () => {
    const nextMonth = new Date(selectedDate);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    return nextMonth <= threeMonthsAhead;
  };

  // 月のイベントデータを取得
  useEffect(() => {
    const fetchMonthEvents = async () => {
      if (!open) return;
      
      try {
        const year = selectedDate.getFullYear();
        const month = selectedDate.getMonth();
        
        // 月の開始日と終了日を計算
        const startDate = new Date(year, month, 1);
        const endDate = new Date(year, month + 1, 0);
        
        // date フィールドを使用してクエリ
        const q = query(
          collection(db, 'tournaments'),
          where('date', '>=', startDate.toISOString().split('T')[0]),
          where('date', '<=', endDate.toISOString().split('T')[0])
        );
        
        const querySnapshot = await getDocs(q);
        const events = {};
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          // date フィールドから日付を取得
          if (data.date) {
            const eventDate = new Date(data.date);
            const day = eventDate.getDate();
            
            if (!events[day]) {
              events[day] = [];
            }
            events[day].push({
              id: doc.id,
              ...data
            });
          }
        });
        
        console.log('Fetched events for month:', year, month + 1, events);
        setMonthEvents(events);
      } catch (error) {
        console.error('Error fetching month events:', error);
        setMonthEvents({});
      }
    };
    
    fetchMonthEvents();
  }, [selectedDate, open]);

  const handleMonthChange = (direction) => {
    const newDate = new Date(selectedDate);
    if (direction === 'prev' && canGoPrev()) {
      newDate.setMonth(newDate.getMonth() - 1);
    } else if (direction === 'next' && canGoNext()) {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setSelectedDate(newDate);
  };

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const handleDateClick = (day) => {
    const newDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), day);
    onDateSelect(newDate);
    
    // 選択した日付にスクロール（リトライ機能付き）
    const scrollToDate = (retryCount = 0) => {
      const targetDateStr = `${newDate.getFullYear()}-${(newDate.getMonth() + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
      const targetElement = document.querySelector(`[data-date="${targetDateStr}"]`);
      
      if (targetElement) {
        // ヘッダーの実際の高さを取得
        const header = document.querySelector('header') || document.querySelector('[class*="StyledHeader"]');
        const headerRect = header ? header.getBoundingClientRect() : null;
        const baseHeaderHeight = headerRect ? headerRect.height : 120; // デフォルト値
        
        // 余白を追加（ヘッダーの高さ + 60px の余白）
        const headerHeight = baseHeaderHeight + 60;
        
        const rect = targetElement.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const targetPosition = Math.max(0, scrollTop + rect.top - headerHeight);
        
        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });
      } else if (retryCount < 5) {
        // 要素が見つからない場合、最大5回まで再試行
        setTimeout(() => scrollToDate(retryCount + 1), 200);
      }
    };
    
    // ポップアップを閉じる
    onClose();
    
    // 月の変更とアニメーションが完了するのを待ってからスクロール
    setTimeout(() => scrollToDate(), 800);
  };

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(selectedDate);
    const firstDay = getFirstDayOfMonth(selectedDate);
    const days = [];
    const dayNames = ['日', '月', '火', '水', '木', '金', '土'];

    // 曜日ヘッダー
    dayNames.forEach((day, index) => {
      days.push(
        <Box
          key={`header-${index}`}
          sx={{
            width: '14.28%',
            textAlign: 'center',
            py: 1,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: index === 0 ? 'error.main' : index === 6 ? 'info.main' : 'text.secondary',
              fontSize: isMobile ? '0.7rem' : '0.8rem',
            }}
          >
            {day}
          </Typography>
        </Box>
      );
    });

    // 空白セル
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <Box
          key={`empty-${i}`}
          sx={{ width: '14.28%' }}
        />
      );
    }

    // 日付セル
    for (let day = 1; day <= daysInMonth; day++) {
      const isToday =
        selectedDate.getFullYear() === today.getFullYear() &&
        selectedDate.getMonth() === today.getMonth() &&
        day === today.getDate();

      const dayOfWeek = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        day
      ).getDay();

      const hasEvents = monthEvents[day] && monthEvents[day].length > 0;

      days.push(
        <Box
          key={day}
          sx={{
            width: '14.28%',
            p: 0.5,
          }}
        >
          <motion.div
            whileHover={hasEvents || isToday ? { scale: 1.1 } : {}}
            whileTap={hasEvents || isToday ? { scale: 0.95 } : {}}
          >
            <Button
              onClick={() => handleDateClick(day)}
              disabled={!hasEvents && !isToday}
              sx={{
                minWidth: 'unset',
                width: '100%',
                aspectRatio: '1',
                p: 0,
                borderRadius: '50%',
                fontSize: isMobile ? '0.875rem' : '1rem',
                fontWeight: isToday ? 700 : hasEvents ? 600 : 400,
                color: isToday ? '#000' : hasEvents ? theme.palette.neon.magenta : 'rgba(255, 255, 255, 0.3)',
                bgcolor: isToday ? 'primary.main' : hasEvents ? 'rgba(255, 0, 255, 0.1)' : 'transparent',
                border: hasEvents && !isToday ? `2px solid ${theme.palette.neon.magenta}` : 'none',
                cursor: hasEvents || isToday ? 'pointer' : 'default',
                '&:hover': {
                  bgcolor: isToday ? 'primary.dark' : hasEvents ? 'rgba(255, 0, 255, 0.2)' : 'transparent',
                  boxShadow: hasEvents ? `0 0 20px ${theme.palette.neon.magenta}` : isToday ? `0 0 15px ${theme.palette.neon.cyan}` : 'none',
                },
                '&.Mui-disabled': {
                  color: dayOfWeek === 0 ? 'rgba(255, 100, 100, 0.3)' : dayOfWeek === 6 ? 'rgba(100, 150, 255, 0.3)' : 'rgba(255, 255, 255, 0.2)',
                  cursor: 'default',
                },
                ...(isToday && {
                  color: '#000',
                  boxShadow: `0 0 20px ${theme.palette.neon.cyan}`,
                }),
                ...(hasEvents && !isToday && {
                  boxShadow: `0 0 10px ${theme.palette.neon.magenta}`,
                }),
              }}
            >
              {day}
            </Button>
          </motion.div>
        </Box>
      );
    }

    return days;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'rgba(10, 10, 10, 0.95)',
          border: '2px solid rgba(0, 255, 255, 0.3)',
          boxShadow: '0 0 40px rgba(0, 255, 255, 0.5)',
          backdropFilter: 'blur(16px)',
        },
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.neon.cyan,
            zIndex: 1,
            '&:hover': {
              transform: 'rotate(90deg)',
              bgcolor: 'rgba(0, 255, 255, 0.1)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent sx={{ pt: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <IconButton
              onClick={() => handleMonthChange('prev')}
              disabled={!canGoPrev()}
              sx={{
                color: !canGoPrev() ? 'rgba(255, 255, 255, 0.3)' : theme.palette.neon.cyan,
                '&:hover': {
                  bgcolor: !canGoPrev() ? 'transparent' : 'rgba(0, 255, 255, 0.1)',
                  transform: !canGoPrev() ? 'none' : 'translateX(-3px)',
                },
                '&.Mui-disabled': {
                  color: 'rgba(255, 255, 255, 0.2)',
                },
              }}
            >
              <ArrowBackIosNewIcon />
            </IconButton>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                letterSpacing: '0.05em',
                color: theme.palette.neon.cyan,
                textAlign: 'center',
              }}
            >
              {selectedDate.getFullYear()}年 {monthNames[selectedDate.getMonth()]}
            </Typography>
            <IconButton
              onClick={() => handleMonthChange('next')}
              disabled={!canGoNext()}
              sx={{
                color: !canGoNext() ? 'rgba(255, 255, 255, 0.3)' : theme.palette.neon.cyan,
                '&:hover': {
                  bgcolor: !canGoNext() ? 'transparent' : 'rgba(0, 255, 255, 0.1)',
                  transform: !canGoNext() ? 'none' : 'translateX(3px)',
                },
                '&.Mui-disabled': {
                  color: 'rgba(255, 255, 255, 0.2)',
                },
              }}
            >
              <ArrowForwardIosIcon />
            </IconButton>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              width: '100%',
            }}
          >
            {renderCalendarDays()}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default DatePickerPopup;
